/**
 * Pure mapping helpers for duplicating a chat.
 *
 * Given a loaded chat and a duplication option, produce the initial
 * `CreateChatFormData` prefill for the create-chat form.
 *
 * NOTE (issue #124): the retired Order Guide editor is NOT duplicated. There is
 * deliberately no order-guide copy and no `parentChatId` — only the ordinary
 * same-buyer / same-distributor field prefill is carried over.
 */

import {
  ChatDetailsResponse,
  ChatUser,
  CreateChatFormData,
  CreateChatType,
  CreatedContext,
  OrderPreference,
  OrderPreferenceType,
} from '@choco-admin/types';

import { getSupplierEntityId } from './editChatMapper';

export type DuplicateChatOption = 'buyer' | 'supplier';

export const isDuplicateChatOption = (
  value: string | null
): value is DuplicateChatOption => value === 'buyer' || value === 'supplier';

const baseData: Partial<CreateChatFormData> = {
  chatType: { value: CreateChatType.ORDERING, label: 'Ordering' },
  createdContext: { value: CreatedContext.ADMIN, label: 'Admin Created' },
};

const mapOrderPreferences = (
  orderPreferences: ChatUser['orderPreferences']
): OrderPreference[] => {
  if (!orderPreferences) {
    return [];
  }

  const preferences: OrderPreference[] = [];
  if (orderPreferences.whatsApp) {
    preferences.push({ type: OrderPreferenceType.WHATSAPP, value: 'whatsapp' });
  }
  if (orderPreferences.sms) {
    preferences.push({ type: OrderPreferenceType.SMS, value: 'sms' });
  }
  if (orderPreferences.fax) {
    preferences.push({ type: OrderPreferenceType.FAX, value: 'fax' });
  }
  return preferences;
};

// Same buyer, different distributor.
const buildBuyerDuplicate = (
  chatData: ChatDetailsResponse
): Partial<CreateChatFormData> => ({
  ...baseData,
  buyer: chatData.buyerEntity
    ? {
        value: chatData.buyerEntity.id,
        label: chatData.buyerEntity.name,
        metaData: {
          fullAddress: chatData.address || '',
          name: chatData.buyerEntity.name,
          referenceId: chatData.buyerEntity.referenceId || '',
        },
      }
    : null,
  buyerUsers: chatData.users
    .filter((user) => !user.supplier)
    .map((user) => ({
      value: user.id,
      label: user.name,
      metaData: {
        email: user.email || '',
        phone: user.phone || '',
        orderPreferences: mapOrderPreferences(user.orderPreferences),
        supplier: false,
        name: user.name,
        referenceId: user.id,
      },
    })),
  deliveryAddress: chatData.address || '',
  additionalEmails: chatData.additionalEmails?.join(', ') || '',
});

// Same distributor, different buyer.
const buildSupplierDuplicate = (
  chatData: ChatDetailsResponse
): Partial<CreateChatFormData> => {
  const supplierEntityId = getSupplierEntityId(chatData);

  return {
    ...baseData,
    supplier:
      chatData.supplierEntity && supplierEntityId
        ? {
            value: supplierEntityId,
            label: chatData.supplierEntity.name,
            metaData: {
              name: chatData.supplierEntity.name,
              supplierId: supplierEntityId,
              phone: '',
              email: '',
              orderPreferences: [],
              supplier: true,
              referenceId: chatData.supplierEntity.referenceId || '',
            },
          }
        : null,
    supplierUsers: chatData.users
      .filter((user) => user.supplier)
      .map((user) => ({
        value: user.id,
        label: user.name,
        metaData: {
          email: user.email || '',
          phone: user.phone || '',
          orderPreferences: mapOrderPreferences(user.orderPreferences),
          supplier: true,
          name: user.name,
          supplierId: supplierEntityId || '',
          referenceId: user.id,
        },
      })),
    chatName: chatData.chatName || '',
  };
};

/**
 * Build the create-chat form prefill for a duplication request.
 * `buyer` keeps the same buyer (different distributor); `supplier` keeps the
 * same distributor (different buyer).
 */
export const buildDuplicateChatData = (
  chatData: ChatDetailsResponse,
  option: DuplicateChatOption
): Partial<CreateChatFormData> =>
  option === 'buyer'
    ? buildBuyerDuplicate(chatData)
    : buildSupplierDuplicate(chatData);
